/** Schemas Zod da Fase 6 (relatórios). */
import { z } from "zod";
import { PERIOD_PRESETS } from "./campaign-enums.js";

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a data no formato AAAA-MM-DD.");

/** Período das métricas (preset ou intervalo). */
export const periodQuerySchema = z.object({
  preset: z.enum(PERIOD_PRESETS).optional(),
  from: isoDay.optional(),
  to: isoDay.optional(),
});
export type PeriodQuery = z.infer<typeof periodQuerySchema>;

export const dashboardQuerySchema = periodQuerySchema.extend({
  clientId: z.uuid().optional(),
  /** Contas específicas (act_...), separadas por vírgula. */
  accounts: z
    .string()
    .trim()
    .regex(/^act_\d{1,30}(,act_\d{1,30})*$/, "Contas inválidas.")
    .optional(),
});
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

export const createReportLinkSchema = z.object({
  clientId: z.uuid("Escolha o cliente."),
  password: z.string().min(4, "A senha precisa ter pelo menos 4 caracteres.").max(100).optional(),
  /** Dias até expirar (null = não expira). */
  expiresInDays: z.number().int().min(1).max(3650).nullable().default(null),
  defaultPreset: z.enum(PERIOD_PRESETS).default("30d"),
});
export type CreateReportLinkInput = z.infer<typeof createReportLinkSchema>;

export const unlockReportSchema = periodQuerySchema.extend({
  password: z.string().max(100).optional(),
});
export type UnlockReportInput = z.infer<typeof unlockReportSchema>;

export const REPORT_FREQUENCIES = ["semanal", "mensal"] as const;
export const REPORT_DELIVERIES = ["pdf", "link"] as const;

export const createReportScheduleSchema = z
  .object({
    clientId: z.uuid("Escolha o cliente."),
    frequency: z.enum(REPORT_FREQUENCIES),
    day: z.number().int().min(1).max(28),
    hour: z.number().int().min(0).max(23).default(8),
    recipients: z
      .array(z.email("E-mail inválido.").transform((v) => v.trim().toLowerCase()))
      .min(1, "Informe pelo menos um e-mail.")
      .max(20),
    delivery: z.enum(REPORT_DELIVERIES).default("pdf"),
    active: z.boolean().default(true),
  })
  .refine((v) => v.frequency !== "semanal" || v.day <= 7, {
    message: "Para o semanal, escolha um dia da semana (1 = segunda … 7 = domingo).",
    path: ["day"],
  });
export type CreateReportScheduleInput = z.infer<typeof createReportScheduleSchema>;

export const updateReportScheduleSchema = z.object({
  frequency: z.enum(REPORT_FREQUENCIES).optional(),
  day: z.number().int().min(1).max(28).optional(),
  hour: z.number().int().min(0).max(23).optional(),
  recipients: z
    .array(z.email("E-mail inválido.").transform((v) => v.trim().toLowerCase()))
    .min(1)
    .max(20)
    .optional(),
  delivery: z.enum(REPORT_DELIVERIES).optional(),
  active: z.boolean().optional(),
});
export type UpdateReportScheduleInput = z.infer<typeof updateReportScheduleSchema>;

export const EXPORT_KINDS = ["campaigns", "adsets", "ads", "dashboard"] as const;
export const EXPORT_FORMATS = ["csv", "xlsx"] as const;

export const exportQuerySchema = periodQuerySchema.extend({
  kind: z.enum(EXPORT_KINDS),
  format: z.enum(EXPORT_FORMATS).default("csv"),
  adAccountMetaId: z
    .string()
    .regex(/^act_\d{1,30}$/)
    .optional(),
  clientId: z.uuid().optional(),
  campaignId: z
    .string()
    .regex(/^\d{1,30}$/)
    .optional(),
  adsetId: z
    .string()
    .regex(/^\d{1,30}$/)
    .optional(),
});
export type ExportQuery = z.infer<typeof exportQuerySchema>;
